import Image from 'next/image'
import * as utils from 'shared/utils'
import Divider from 'shared/ui/Divider'
import Link from 'next/link'
import ilja from 'public/details/ilja.jpeg'
import katja from 'public/details/katja.jpeg'
import nik from 'public/details/nik.jpeg'
import tommy from 'public/details/tommy.jpeg'
import { down } from 'styled-breakpoints'
import checker from 'public/checker.png'
import styled from 'styled-components'
import Page from 'shared/ui/Page'
import { requestGeneses, useGeneses } from 'shared/api'
import { GetStaticPathsResult, GetStaticPropsResult } from 'next'
import { Genesis as GenesisType } from 'shared/types'

const ASSETS_CDN = process.env.NEXT_PUBLIC_ASSETS_CDN

enum ArtistNames {
  ilja = 'ilja',
  katja = 'katja',
  nik = 'nik',
  tommy = 'tommy'
}

const images = {
  ilja,
  katja,
  nik,
  tommy
}

const Artist: React.FC<ArtistProps> = ({ geneses, artist, meta }) => {
  const { geneses: liveGeneses } = useGeneses()
  const cachedGeneses = JSON.parse(geneses) as GenesisType[]

  const liveArtistGeneses = cachedGeneses.map(({ genesisIdx, ...metaData }) => ({
    ...metaData,
    ...liveGeneses?.find(({ genesisIdx: id }) => id === genesisIdx)
  }))

  const usedGeneses = liveGeneses ? liveArtistGeneses : cachedGeneses

  console.log('using', liveGeneses ? 'live geneses' : 'cached geneses')

  const firstname = artist?.name?.split(' ')?.[0]?.toLowerCase() as keyof typeof ArtistNames

  return (
    <Page>
      {artist && (
        <Content>
          <Name>
            {artist.name}
          </Name>

          <Text>
            <ArtistDetails>
              <Description>
                <div>
                  {artist.description}
                </div>
              </Description>
            </ArtistDetails>

            <ArtistImage style={{ backgroundImage: `url("${images[firstname].src}")` }} />
          </Text>

          <Divider>
            <a target="_blank" href={artist.instagram.link}>
              Instagram
            </a>
          </Divider>

          <Gallery>
            {usedGeneses.map((genesis) => {
              const [activeGeneration] = genesis?.generations ? [...genesis.generations].reverse() : [[0,0,0,0]]
              return (
                <Genesis key={genesis.genesisIdx}>
                  <Link href={`/gallery/${genesis.genesisIdx}`} passHref>
                    <ImageContainer>
                      <Image
                        src={`${ASSETS_CDN}/print/${genesis.genesisIdx}/${activeGeneration.join('')}.png`}
                        layout="responsive"
                        width={2480}
                        height={3508}
                        quality={100}
                        sizes={`20vw`}
                      />
                    </ImageContainer>
                  </Link>
                </Genesis>
              )
            })}
            {10 - usedGeneses.length > 0 && new Array((10 - usedGeneses.length)).fill(undefined).map((_, i) => (
              <Genesis key={`extra-${i}`}>
                <ImageContainer>
                  <EmptyImage src={checker.src} />
                </ImageContainer>
              </Genesis>
            ))}
          </Gallery>
        </Content>
      )}
    </Page>
  )
}

export default Artist

interface ArtistProps {
  geneses: string
  meta: any
  artist: utils.Artist
}

export async function getStaticProps({ params }: { params: { artistId: string} }): Promise<GetStaticPropsResult<ArtistProps>> {
  const { artistId } = params
  const artist = utils.artists[artistId]
  const meta = await (await fetch(`${ASSETS_CDN}/genesis/metadata.json`)).json()
  const allGeneses = await requestGeneses()
  const artistGeneses = meta?.filter(
    (genesis: any) => genesis.traits.Artist === artist?.name
  )
  const geneses = artistGeneses.map(({ genesisIdx, ...metaData }: any) => ({
    ...metaData,
    ...allGeneses?.find(({ genesisIdx: id }) => id === genesisIdx)
  }))

  return {
    props: {
      geneses: JSON.stringify(geneses),
      meta,
      artist
    },
    revalidate: 60
  }
}

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  return {
    paths: Object.keys(utils.artists).map((artistId) => ({
      params: {
        artistId
      }
    })),
    fallback: false
  }
}

const EmptyImage = styled.div`
  width: 100%;
  height: 100%;
  border: 1px solid var(--color-black) !important;
  background-position-x: -8px;
  ${(props: { src: string }) => (
    `background-image: url('${props.src}');`
  )}
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
`

const Text = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
  grid-auto-rows: 1fr;
  column-gap: var(--gutter);
  row-gap: var(--gutter);

  ${down('small')} {
    grid-template-columns: 1fr 1fr;
    grid-auto-rows: initial;
  }
`

const ArtistDetails = styled.div`
  grid-column-start: 1;
  grid-column-end: 4;

  ${down('small')} {
    grid-column-end: 6;
  }
`

const Name = styled.div`
  margin-bottom: 40px;
  font-size: 2.1875rem;
  line-height: 145%;
  align-items: center;
  font-weight: bold;

  width: 100%;

  ${down('small')} {
    text-align: center;
  }
`

const Description = styled.div`
  font-size: 1.5625rem;
  line-height: 145%;
  text-align: justify;
  letter-spacing: -0.01em;

  display: flex;
  flex-direction: row;

  ${down('medium')} {
    flex-direction: column-reverse;
  }
`

const ArtistImage = styled.div`
  border: 1px solid var(--color-black) !important;

  background-repeat: no-repeat;
  background-size: cover;
  background-position: center center;

  grid-column-start: 4;
  grid-column-end: 6;

  ${down('small')} {
    grid-column-start: 1;
    grid-column-end: 6;
    grid-row: 1;
    height: 40vh;
  }
`

const Gallery = styled.div`
  display: grid;
  width: 100%;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
  grid-auto-rows: 1fr;
  column-gap: var(--gutter);
  row-gap: var(--gutter);

  ${down('small')} {
    grid-template-columns: 1fr 1fr;
  }
`

const Genesis = styled.div`
  height: 100%;
`

const ImageContainer = styled.a`
  display: block;
  width: 100%;
  height: 100%;
  min-height: 14rem;
  img {
    border: 1px solid var(--color-black) !important;
  }import { BigNumber } from '@ethersproject/bignumber'

`
